"use client";
import { z } from "zod";
import { Form } from "@/app/global-components/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FaSpinner } from "react-icons/fa";
import { toast } from "react-toastify";
import { Box, Typography } from "@mui/material";

import ConfirmFormFieldView from "./confirm-form-field-view";
import ConfirmSubmitView from "./confirm-submit-view";
import ResendCode from "./resend-code";
import VerifiedEmail from "./verified-email";
import useConfirm from "@/hooks/use-confirm";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import { ConfirmSchema } from "@/schema";

const UnifiedConfirmView = () => {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  // const theme = useTheme();
  const [searchParams] = useSearchParams();
  const [confirmed, setConfirmed] = useState(false);
  const [isUrlConfirmation, setIsUrlConfirmation] = useState(false);

  const { confirm, resendCode, isLoading } = useConfirm();

  const form = useForm<z.infer<typeof ConfirmSchema>>({
    resolver: zodResolver(ConfirmSchema),
  });

  const urlCode = searchParams.get("code");
  const urlEmail = searchParams.get("email");
  const formEmail = form.watch("email");

  // Check if we have URL parameters for automatic confirmation
  useEffect(() => {
    if (urlCode && urlEmail) {
      setIsUrlConfirmation(true);
      handleUrlConfirmation();
    }
  }, [urlCode, urlEmail]);

  const handleUrlConfirmation = async () => {
    if (urlCode && urlEmail) {
      try {
        const response = await confirm({ email: urlEmail, code: urlCode });
        if (response) {
          setConfirmed(true);
        }
      } catch (err) {
        setConfirmed(false);
        console.log(err);
      }
    }
  };

  const handleResendCode = async () => {
    const emailToUse = urlEmail || formEmail;
    if (emailToUse) {
      try {
        await resendCode(emailToUse);
      } catch (err) {
        // Error handling is done in the hook
      }
    } else {
      toast.error(t("alerts.error.resendCodeEmailError"));
    }
  };

  const onSubmit = async (data: z.infer<typeof ConfirmSchema>) => {
    try {
      const response = await confirm({ email: data.email, code: data.code });
      if (response) {
        setConfirmed(true);
      }
    } catch (err) {
      setConfirmed(false);
      console.log(err);
    }
  };

  // Loading state for URL-based confirmation
  if (isUrlConfirmation && isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          py: 4,
        }}
      >
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            animation: "spin 1s linear infinite",
            "@keyframes spin": {
              "0%": { transform: "rotate(0deg)" },
              "100%": { transform: "rotate(360deg)" },
            },
          }}
        >
          <FaSpinner size={24} />
        </Box>
      </Box>
    );
  }

  // Success state
  if (confirmed) {
    return <VerifiedEmail />;
  }

  // URL confirmation failed - show resend option
  if (isUrlConfirmation && !confirmed && urlEmail) {
    return <ResendCode onClick={() => handleResendCode()} />;
  }

  // Manual form confirmation
  return (
    <Form {...form}>
      <Box
        component="form"
        onSubmit={form.handleSubmit(onSubmit)}
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
      >
        <Box sx={{ textAlign: "right" }}>
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              fontSize: "0.75rem",
              cursor: "pointer",
              "&:hover": {
                color: "primary.main",
                textDecoration: "underline",
              },
              pointerEvents: isLoading ? "none" : "auto",
              opacity: isLoading ? 0.6 : 1,
            }}
            onClick={handleResendCode}
          >
            {isLoading ? t("status.sending") : t("actions.resendCode")}
          </Typography>
        </Box>

        <ConfirmFormFieldView
          name="email"
          label={t("authentication.fields.email")}
          placeholder={t("profile.placeholders.enterEmail")}
          inputType="text"
          formControl={form.control}
        />
        <ConfirmFormFieldView
          name="code"
          label={t("authentication.fields.token")}
          placeholder={t("authentication.placeholders.enterToken")}
          inputType="text"
          formControl={form.control}
        />
        <ConfirmSubmitView isLoading={isLoading} />
      </Box>
    </Form>
  );
};

export default UnifiedConfirmView;
